import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, User, Stethoscope, CalendarDays, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import StepIndicator from "../components/StepIndicator";
import BackButton from "../components/BackButton";
import PrimaryButton, { GhostButton } from "../components/PrimaryButton";
import { confirmarAgendamento } from "../lib/api";
import { getBooking, setBooking, clearBooking } from "../lib/bookingState";
import { formatDateTime } from "../lib/format";
import ParticularWarningModal from "../components/ParticularWarningModal";

const STEPS = ["CPF", "Especialidade", "Profissional", "Horario", "Confirmar"];

export default function AgendarConfirmar() {
  const navigate = useNavigate();
  const booking = getBooking();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showNaoAtendeConvenio, setShowNaoAtendeConvenio] = useState(false);

  if (!booking.patient_id || !booking.profissional_id || !booking.data_hora) {
    return (
      <Layout>
        <div className="text-slate-600">Sessão expirada. Recomece o agendamento.</div>
        <div className="mt-4">
          <PrimaryButton onClick={() => navigate("/agendar", { replace: true })}>
            Voltar
          </PrimaryButton>
        </div>
      </Layout>
    );
  }

  async function enviarConfirmacao(forcarParticular = false) {
    const r = await confirmarAgendamento({
      patient_id: booking.patient_id,
      professional_id: booking.profissional_id,
      especialidade_id: booking.especialidade_id || "",
      data_hora: booking.data_hora,
      procedimento_id: booking.procedimento_id || 100,
      local_id: booking.local_id || 17,
      forcar_particular: Boolean(forcarParticular),
    });

    setBooking({ confirmacao: r });
    toast.success("Agendamento realizado com sucesso!");
    navigate("/sucesso?type=novo", { replace: true });
  }

  async function handleConfirm() {
    if (submitting) return;

    setSubmitting(true);
    setError("");

    try {
      await enviarConfirmacao(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;

      if (detail === "medico_nao_atende_convenio") {
        setSubmitting(false);
        setShowNaoAtendeConvenio(true);
        return;
      }

      setError("Não foi possível confirmar agora. Tente novamente em instantes.");
    } finally {
      setSubmitting(false);
    }
  }

  function cancel() {
    clearBooking();
    navigate("/", { replace: true });
  }

  async function confirmarComoParticular() {
    setShowNaoAtendeConvenio(false);
    setSubmitting(true);
    setError("");

    try {
      await enviarConfirmacao(true);
    } catch {
      setError("Não foi possível confirmar como particular agora. Tente novamente em instantes.");
    } finally {
      setSubmitting(false);
    }
  }

  function cancelarParticular() {
    setShowNaoAtendeConvenio(false);
    setError("Selecione outro profissional que atenda seu convênio ou continue como particular.");
  }

  return (
    <Layout>
      <BackButton to="/agendar/horarios" />
      <StepIndicator steps={STEPS} current={5} />

      <div className="fade-up">
        <h1
          className="text-2xl sm:text-3xl tracking-tight font-bold text-slate-900"
          style={{ fontFamily: "Manrope" }}
        >
          Confirmar agendamento
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          Revise as informações abaixo antes de confirmar.
        </p>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="confirm-summary">
        <Row icon={<User size={18} />} label="Paciente" value={booking.paciente_nome} />
        <Row icon={<Stethoscope size={18} />} label="Especialidade" value={booking.especialidade_nome} />
        <Row icon={<CheckCircle2 size={18} />} label="Tipo" value={booking.procedimento_nome || "Consulta"} />
        <Row icon={<CheckCircle2 size={18} />} label="Profissional" value={booking.profissional_nome} />
        <Row icon={<CalendarDays size={18} />} label="Data e horário" value={formatDateTime(booking.data_hora)} />
      </div>

      {error && (
        <div className="mt-5 flex items-start gap-2 bg-red-50 text-red-700 border border-red-100 rounded-xl px-4 py-3 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="mt-6 space-y-3">
        <PrimaryButton onClick={() => handleConfirm()} disabled={submitting} data-testid="confirm-submit">
          {submitting ? "Confirmando..." : "Confirmar agendamento"}
        </PrimaryButton>

        <GhostButton onClick={cancel} disabled={submitting} data-testid="confirm-cancel">
          Cancelar
        </GhostButton>
      </div>

      <ParticularWarningModal
        open={showNaoAtendeConvenio}
        title="O profissional não atende seu convênio"
        message="O convênio vinculado ao seu cadastro não é aceito por esse profissional."
        onCancel={cancelarParticular}
        onConfirm={confirmarComoParticular}
      />
    </Layout>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="px-5 py-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-[#E6EDF5] text-[#00357C] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</div>
        <div className="text-slate-900 font-medium mt-0.5 break-words">{value || "-"}</div>
      </div>
    </div>
  );
}